// @flow

import React, { Component } from "react";
import { reject, filter, map, isEqual, get, noop } from "lodash";
import type { Changes, RaceTypeCodeEnum } from "@tvg/types/Race";
import countRaceChanges from "@tvg/utils/raceChanges";
import type { UnaryFn } from "@tvg/types/Functional";

import MessageBox from "../../_molecule/MessageBox";
import Title from "../../_atom/Title";
import TitleHeader from "../../_molecule/TitleHeader";
import ExpandableRow from "../ExpandableRow";
import RaceChanges from "../RaceChanges";
import Icon from "../../_static/Icons";
import { changes, arrowDown } from "../../_static/Icons/icons";

import buildColor from "../../_static/ColorPalette";

import {
  Container,
  ExpandAll,
  ExpandAllText,
  IconContainer,
  Races,
  RaceRow,
  RaceHeader,
  ChangesNumberContainer,
  ChangesNumber
} from "./styled-components";

export type ChangesGTM = {
  action: string,
  section: string,
  raceNumber: number
};

type Race = {
  number: number,
  changes: Changes,
  raceTypeCode: RaceTypeCodeEnum
};

type Props = {
  races: Race[],
  trackName: string,
  trackCode: string,
  raceNumber: number,
  triggerGTMEvent: UnaryFn<ChangesGTM, void>
};

type State = {
  expandedRaces: string[],
  racesWithChanges: Race[],
  raceNumber: number
};

const allRacesExpanded = (state: State) =>
  state.expandedRaces.length === state.racesWithChanges.length;

export default class AllRacesChanges extends Component<Props, State> {
  static defaultProps = {
    races: [],
    trackName: "",
    trackCode: "",
    raceNumber: 0,
    triggerGTMEvent: noop
  };

  constructor(props: Props) {
    super(props);

    this.state = {
      racesWithChanges: [],
      expandedRaces: [],
      raceNumber: this.props.raceNumber
    };
  }

  shouldComponentUpdate(nextProps: Props, nextState: State) {
    return (
      this.state.racesWithChanges.length !==
        nextState.racesWithChanges.length ||
      this.state.expandedRaces.length !== nextState.expandedRaces.length ||
      !isEqual(this.props, nextProps)
    );
  }

  static getDerivedStateFromProps(props: Props, state: State) {
    const newChanges = filter(props.races, (race) => race.changes);
    return !isEqual(newChanges, state.racesWithChanges) ||
      state.raceNumber !== props.raceNumber
      ? {
          raceNumber: props.raceNumber,
          expandedRaces: [],
          racesWithChanges: newChanges
        }
      : null;
  }

  scrollToElement = (
    isOpen: boolean,
    elementId: string = "allRacesChanges"
  ) => {
    const domEl = document.getElementById(elementId);
    if (isOpen && domEl) {
      setTimeout(() => {
        // $FlowBug
        domEl.scrollIntoView({
          behavior: "smooth",
          block: "start"
        });
      }, 500);
    }
  };

  toggleExpandAll = () => {
    this.props.triggerGTMEvent({
      action: allRacesExpanded(this.state) ? "Collapse" : "Expand",
      section: "All changes",
      raceNumber: this.props.raceNumber
    });
    this.setState(
      (prevState) => ({
        expandedRaces: allRacesExpanded(prevState)
          ? []
          : [
              ...map(
                prevState.racesWithChanges,
                (race) => `RaceChanges-${race.number}`
              )
            ]
      }),
      () => {
        this.scrollToElement(true);
      }
    );
  };

  handleExpand = (isOpen: boolean, id: string, raceNumber: number) => {
    this.props.triggerGTMEvent({
      action: isOpen ? "Collapse" : "Expand",
      section: "Race changes",
      raceNumber
    });

    this.setState((prevState) => ({
      expandedRaces: !isOpen
        ? [...prevState.expandedRaces, id]
        : reject(prevState.expandedRaces, (raceId) => raceId === id)
    }));

    this.scrollToElement(isOpen, id);
  };

  renderRaces = () =>
    map(this.state.racesWithChanges, (race) => {
      const raceId = `RaceChanges-${race.number}`;
      return (
        race.changes && (
          <RaceRow key={raceId}>
            <ExpandableRow
              isOpen={this.state.expandedRaces.indexOf(raceId) !== -1}
              header={
                <RaceHeader id={raceId}>
                  <Title fontSize={14}>{`Race ${race.number}`}</Title>
                  <ChangesNumberContainer>
                    <Icon
                      icon={changes}
                      size={15}
                      color={buildColor("yellow", "500")}
                    />
                    <ChangesNumber>
                      {countRaceChanges(race.changes)}
                    </ChangesNumber>
                  </ChangesNumberContainer>
                </RaceHeader>
              }
              onExpand={(isOpen) =>
                this.handleExpand(isOpen, raceId, race.number)
              }
            >
              <RaceChanges
                changes={race.changes}
                raceNumber={race.number}
                raceTypeCode={get(race, "type.code", "")}
              />
            </ExpandableRow>
          </RaceRow>
        )
      );
    });

  renderRacesHeader = () => (
    <TitleHeader
      title={`All ${this.props.trackCode} changes`}
      subTitle={
        this.state.racesWithChanges.length ? (
          <ExpandAll onClick={this.toggleExpandAll}>
            <ExpandAllText>
              {allRacesExpanded(this.state) ? "Collapse All" : "Expand All"}
            </ExpandAllText>
            <IconContainer isOpen={allRacesExpanded(this.state)}>
              <Icon
                icon={arrowDown}
                size={16}
                color={buildColor("grey", "900")}
              />
            </IconContainer>
          </ExpandAll>
        ) : null
      }
    />
  );

  render() {
    return (
      <Races id="allRacesChanges">
        {this.renderRacesHeader()}
        <Container isEmpty={!this.state.racesWithChanges.length}>
          {this.state.racesWithChanges.length ? (
            this.renderRaces()
          ) : (
            <MessageBox
              isVertical
              message={
                <div>
                  Currently there are no changes for any <br /> races at&nbsp;
                  {this.props.trackName}
                </div>
              }
            />
          )}
        </Container>
      </Races>
    );
  }
}
